/* HeroStageCard — image cover on the front, the existing pathway card on the back.
   Desktop (hover + fine pointer): flips on hover, stays while focus is inside.
   Touch / keyboard: first activation reveals the card; "Back to cover" returns. */
import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Undo2 } from 'lucide-react';
import { useTheme } from '../lib/theme';

export const HERO_STAGE_CARD_CSS = `
  .hsc { perspective: 1400px; position: relative; }
  .hsc-inner { position: relative; width: 100%; height: 100%; transform-style: preserve-3d; transition: transform 560ms cubic-bezier(0.4, 0, 0.2, 1); }
  .hsc[data-revealed="true"] .hsc-inner { transform: rotateY(180deg); }
  @media (hover: hover) and (pointer: fine) {
    .hsc:hover .hsc-inner, .hsc:has(.hsc-back :focus) .hsc-inner { transform: rotateY(180deg); }
  }
  .hsc-face { position: absolute; inset: 0; backface-visibility: hidden; -webkit-backface-visibility: hidden; transform: translateZ(0); }
  .hsc-back { transform: rotateY(180deg) translateZ(1px); }
  .hsc-front { transform: rotateY(0deg) translateZ(1px); }
  .hsc-cover { width: 100%; height: 100%; object-fit: contain; object-position: center; display: block; }
  /* Sparkle ring — brief, border only, never intercepts clicks */
  .hsc::after { content: ''; position: absolute; inset: -3px; border-radius: 1.7rem; padding: 2.5px; pointer-events: none; opacity: 0; z-index: 2;
    background: conic-gradient(from var(--hsc-a, 0deg), transparent 0 60%, rgba(13,148,136,0.95) 70%, rgba(251,191,36,0.95) 80%, transparent 90%);
    -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0); -webkit-mask-composite: xor; mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0); mask-composite: exclude; }
  @property --hsc-a { syntax: '<angle>'; inherits: false; initial-value: 0deg; }
  @keyframes hscSparkle { 0% { opacity: 0; --hsc-a: 0deg; } 15% { opacity: 1; } 100% { opacity: 0; --hsc-a: 360deg; } }
  .hsc[data-revealed="true"]::after { animation: hscSparkle 750ms ease-out forwards; }
  @media (hover: hover) and (pointer: fine) { .hsc:hover::after { animation: hscSparkle 750ms ease-out forwards; } }
  .hsc-hint { display: none; }
  /* Premium glow + lift on hover */
  .hero-stage-card { --glow: rgba(13,148,136,.32); --glow-ring: rgba(20,184,166,.55); }
  html.dark .hero-stage-card { --glow: rgba(45,212,191,.38); --glow-ring: rgba(45,212,191,.6); }
  .hero-stage-card .hsc-front > button, .hero-stage-card .hsc-back > a, .hero-stage-card:not(.hsc) > a {
    transition: transform 340ms cubic-bezier(.22,1,.36,1), box-shadow 340ms ease, border-color 300ms ease; will-change: transform; }
  @media (hover: hover) and (pointer: fine) {
    .hero-stage-card:hover .hsc-front > button, .hero-stage-card:hover .hsc-back > a, .hero-stage-card:not(.hsc):hover > a {
      transform: translateY(-7px);
      box-shadow: 0 22px 44px -14px var(--glow), 0 0 0 1px var(--glow-ring), 0 0 28px -4px var(--glow) !important; }
    .hero-stage-card:hover .hsc-back > a { border-color: var(--glow-ring) !important; }
  }
  @media (hover: none) { .hsc-hint { display: inline-flex; } }
  @media (prefers-reduced-motion: reduce) {
    .hsc-inner { transition: none; }
    .hsc::after { animation: none !important; display: none; }
  }
`;

export default function HeroStageCard({ s, idx, cover }) {
  const [revealed, setRevealed] = useState(false);
  const [failed, setFailed] = useState(false);
  const { isDark } = useTheme();
  const linkRef = useRef(null);
  const coverRef = useRef(null);
  const flipEnabled = !!cover && !failed;
  const reveal = () => { setRevealed(true); setTimeout(() => linkRef.current?.focus({ preventScroll: true }), 300); };
  const hide = () => { setRevealed(false); setTimeout(() => coverRef.current?.focus({ preventScroll: true }), 300); };

  const back = (
    <Link
      ref={linkRef}
      to={s.to}
      aria-label={s.aria}
      data-testid={`hero-stage-${s.key}`}
      tabIndex={flipEnabled && !revealed ? -1 : 0}
      className={[
        'group relative flex flex-col bg-white rounded-3xl p-5 md:p-6 border-2 transition-[border-color,box-shadow] duration-300 h-full w-full',
        s.border, s.shadow, 'hover:shadow-2xl active:scale-[0.985] focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2',
      ].join(' ')}
    >
      <div className={`w-12 h-12 rounded-2xl ${s.iconBg} text-white flex items-center justify-center shadow-md group-hover:scale-110 transition-transform`}>
        <s.Icon size={22} strokeWidth={2.2} />
      </div>
      <div className="mt-auto pt-4">
        <div className={`inline-block text-[10px] font-bold tracking-widest uppercase px-2 py-1 rounded ${s.chip} mb-2`}>{s.label}</div>
        <div className="font-bold text-gray-900 text-lg leading-snug">{s.headline}</div>
        <p className="text-sm text-gray-600 mt-1 leading-relaxed line-clamp-3">{s.sub}</p>
      </div>
      <div className="absolute top-5 right-5 w-8 h-8 rounded-full bg-gray-100 group-hover:bg-gray-900 text-gray-500 group-hover:text-white flex items-center justify-center transition-all">
        <ArrowUpRight size={16} />
      </div>
    </Link>
  );

  if (!flipEnabled) {
    return (
      <div className="hero-stage-card h-[260px] md:h-[280px]" style={{ animationDelay: `${idx * 70}ms` }} data-testid={`hero-stage-wrap-${s.key}`}>
        {back}
      </div>
    );
  }

  return (
    <div
      className="hsc hero-stage-card h-[260px] md:h-[280px]"
      style={{ animationDelay: `${idx * 70}ms` }}
      data-revealed={revealed ? 'true' : 'false'}
      data-testid={`hero-stage-wrap-${s.key}`}
    >
      <div className="hsc-inner">
        {/* FRONT — image cover */}
        <div className="hsc-face hsc-front">
          <button
            ref={coverRef}
            type="button"
            onClick={reveal}
            aria-expanded={revealed}
            aria-controls={`hsc-back-${s.key}`}
            aria-label={`Show ${s.headline} details — for ${s.who}`}
            tabIndex={revealed ? -1 : 0}
            data-testid={`hero-stage-cover-${s.key}`}
            className={`block w-full h-full rounded-3xl border-2 overflow-hidden text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2 ${s.border}`}
            style={{ background: isDark ? '#12161D' : '#FFF8EC' }}
          >
            <img src={isDark ? cover.src.replace('/cards/', '/cards/dark/') : cover.src} alt={cover.alt} className="hsc-cover" loading={idx < 4 ? 'eager' : 'lazy'} decoding="async" onError={() => setFailed(true)} />
            <span className="hsc-who absolute left-3 bottom-3 max-w-[calc(100%-1.5rem)] inline-flex items-center gap-1.5 rounded-full pl-1 pr-3 py-1 bg-gray-950/70 text-white backdrop-blur-md ring-1 ring-white/15 shadow-lg pointer-events-none" data-testid={`hero-stage-who-${s.key}`} aria-hidden>
              <span className="shrink-0 inline-flex items-center rounded-full bg-teal-400 text-gray-950 text-[9px] font-black uppercase tracking-[0.14em] px-1.5 py-0.5">For</span>
              <span className="truncate text-[11.5px] md:text-xs font-semibold tracking-tight leading-none">{s.who}</span>
            </span>
            <span className="hsc-hint absolute top-3 right-3 items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-gray-700 bg-white/85 backdrop-blur rounded-full px-2 py-1 pointer-events-none" aria-hidden>
              Tap to open
            </span>
          </button>
        </div>
        {/* BACK — the existing card, unchanged */}
        <div className="hsc-face hsc-back" id={`hsc-back-${s.key}`}>
          {back}
          <button
            type="button"
            onClick={hide}
            aria-label={`Back to ${s.headline} cover image`}
            tabIndex={revealed ? 0 : -1}
            data-testid={`hero-stage-back-${s.key}`}
            className={`${revealed ? 'inline-flex' : 'hidden'} absolute top-5 right-[60px] items-center gap-1 text-[11px] font-bold text-gray-700 bg-white border border-gray-200 shadow-sm rounded-full px-2.5 py-1.5 min-h-[32px] hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500`}
          >
            <Undo2 size={12} /> Back to cover
          </button>
        </div>
      </div>
    </div>
  );
}
